import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import { includes, isEmpty, isNil, orderBy } from 'lodash'
import { useTranslation } from 'react-i18next'
import { VehicleVcf } from '../common/vehicle-info/VehicleVcf'
import { AssignInventoryVehicleItem } from './AssignInventoryVehicleItem'
import { PreRegistration } from '../common/pre-registration/PreRegistration'
import { EmptyRun } from './EmptyRun'
import {
    DnDDragTypeConstant,
    EMPTY_STRING,
    RUN_LANE_MAX_LENGTH,
    RUN_LANE_NUMBER_OF_SHOW_MORE,
    ToggleFunction
} from '../../utils/constant'
import {isPreRegisterVehicle, isReserveVehicle, isSentToAs400} from '../../utils/vehicle-utils'
import { isEnableToggleFlag } from '../../utils/utils'
import { getPreRegistrations } from '../../utils/data-source/pre-registrations-datasource'
import DynamicSizeList from '../common/dynamic-size-list/DynamicSizeList'
import AssignVehicleListFooter from './AssignVehicleListFooter'
import classNames from 'classnames'
import { useVehicleDraggingMonitor } from '../../hooks/assign-inventory/UseVehicleDraggingMonitor'
import { useDispatch, useSelector } from 'react-redux'
import { getVehicleAfterUpdatePreRegist } from '../../actions/PreRegistAction'
import usePushQuickSearchValue from '../../hooks/common/UsePushQuickSearchValue'
import compareVehicles, {
    isSaleTypeChangedSinceLastSend,
    removeElementFromList,
    resetVehicleDetailInput,
    upsertElementInList
} from './helper/VehicleHelper'
import { VehicleVcfEdit } from '../common/vehicle-info/VehicleVcfEdit'
import { v4 as uuid } from 'uuid'
import { getPatErrorMessage, getSaleTypesEligibility } from '../../utils/data-source/pat-datasource'
import {
    discardPatEligibility,
    receivePatEligibilityError,
    receivePatEligibilitySuccess,
    requestPatEligibility,
    retryPatEligibility
} from '../../actions/PatEligibilityAction'
import { getPatEligibilityKey } from '../../reducers/PatEligibilityReducer'

export function AssignVehicleList({
    auctionCode,
    isShowChecked,
    draftVehicles,
    setDraftVehicles,
    laneInventory,
    setLaneInventory,
    runRanges,
    laneNumber,
    saleYear,
    saleNumber,
    setIsGetPreRegistError,
    bulkUpdateVehicleErrors,
    isSendToAS400Blocked,
    scrollToItem
}) {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const [emptyRuns, setEmptyRuns] = useState([])
    const [vehicles, setVehicles] = useState([])
    const [sblu, setSblu] = useState(null)
    const [isVcfOpen, setIsVcfOpen] = useState(false)
    const [isVcfEditOpen, setIsVcfEditOpen] = useState(false)
    const [messageUpdateVehicleSuccessful, setMessageUpdateVehicleSuccessful] = useState([])
    const [isPreRegistrationOpen, setIsPreRegistrationOpen] = useState(false)
    const [preRegistrationData, setPreRegistrationData] = useState(null)
    const [holderVehicles, setHolderVehicles] = useState(new Map())
    const [titleVehicle, setTitleVehicle] = useState(null)
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const { isDragging, draggingItem } = useVehicleDraggingMonitor()
    const assignedVehiclesRef = useRef(null)
    const inFlightPatRequestsRef = useRef(new Set())
    const { search } = useSelector(states => states.resetStateObservingContext.assignInventory);
    const quickSearchContext = useSelector(states => states.inventoryQuickSearchContext.quickSearchContext)
    const toggleConfigs = useSelector(states => states.session.toggleConfigs)
    const patEligibilityEntriesByKey = useSelector(states => states.patEligibilityContext.entriesByKey)
    const isPatSaleTypeIntegrationEnabled = isEnableToggleFlag(
        toggleConfigs[ToggleFunction.ENABLE_PAT_SALE_TYPE_RECOMMENDATION],
        auctionCode
    )
    const { pushQuickSearchValue } = usePushQuickSearchValue()

    useEffect(() => {
        setMessageUpdateVehicleSuccessful([])
    }, [search])

    useEffect(() => {
        initRuns()
    }, [runRanges])

    // Reset show all when next available button state is false
    useEffect(() => {
        if (!isShowChecked) {
            const emptySpots = buildEmptySpots()
            setEmptyRuns(emptySpots)
            const vehicleWithoutEmptySpots = vehicles.filter(vehicle => vehicle.status !== "EMPTY")
            initVehicles(vehicleWithoutEmptySpots, emptySpots)
        }
    }, [isShowChecked])

    function initRuns() {
        var emptySpots = buildEmptySpots()
        setEmptyRuns(emptySpots)

        laneInventory.forEach((vehicle) => {
            if (isPreRegisterVehicle(vehicle) || isReserveVehicle(vehicle) || isSentToAs400(vehicle)) {
                holderVehicles.set(vehicle.runNumber, vehicle)
            }
        })
        setHolderVehicles(holderVehicles)

        let laneVehicles = laneInventory.filter((l)=>{
            return draftVehicles.map((d)=>d.runNumber).indexOf(l.runNumber) == -1
        })

        let mergedLaneVehicles = []
        draftVehicles.forEach(draftVehicle => {
            let newVehicle = {...draftVehicle}
            newVehicle.replacedItem = laneInventory.find(p => p.runNumber === newVehicle.runNumber)
            mergedLaneVehicles.push(newVehicle)
        })

        setDraftVehicles([...mergedLaneVehicles])

        let orderedVehicleList = [
            ...laneVehicles,
            ...mergedLaneVehicles
        ].sort((a, b) => a.runNumber - b.runNumber)

        initVehicles(orderedVehicleList, emptySpots)
    }

    function buildEmptySpots() {
        var emptySpots = []
        for (var i = 0; i < runRanges.length; i++) {
            if (runRanges[i].type === 'EMPTY') {
                for (
                    var j = runRanges[i].startingRunNumber;
                    j <= runRanges[i].endingRunNumber;
                    j++
                ) {
                    emptySpots.push(j)
                }
            }
        }
        return emptySpots
    }

    function initVehicles(orderedVehicleList, emptySpots) {
        var updatedList = []
        let lastEmptySpotPosition = 0
        let last_vehicle_position = 0
        while (last_vehicle_position < orderedVehicleList.length) {
            var vehicle = orderedVehicleList[last_vehicle_position]
            vehicle = {...vehicle,
                fromVehicle: {
                    saleYear: vehicle.origSaleYear,
                    saleNumber: vehicle.origSaleNumber,
                    laneNumber: vehicle.origLaneNumber,
                    runNumber: vehicle.origRunNumber
                }
            }
            var emptySpot = emptySpots[lastEmptySpotPosition]
            if (vehicle && Number(emptySpot) < Number(vehicle.runNumber)) {
                updatedList.push({status: "EMPTY", runNumber: emptySpot + ''})
                lastEmptySpotPosition++
            } else if (
                vehicle &&
                Number(emptySpot) > Number(vehicle.runNumber)
            ) {
                updatedList.push(vehicle)
                last_vehicle_position++
            } else {
                updatedList.push(vehicle)
                lastEmptySpotPosition++
                last_vehicle_position++
            }
        }

        addEmptyRuns(updatedList, emptySpots, lastEmptySpotPosition, RUN_LANE_NUMBER_OF_SHOW_MORE)
        setVehicles(updatedList)
    }

    function addEmptyRuns(updatedList, emptySpots, lastEmptySpotPosition, numberOfRun) {
        Array.from({length: numberOfRun}, (_, i) => {
            if(Number(emptySpots[lastEmptySpotPosition + i]) <= RUN_LANE_MAX_LENGTH) {
                updatedList.push({status: "EMPTY", runNumber: emptySpots[lastEmptySpotPosition + i] + ''})
            } else {
                return
            }
        })
    }

    function updateVehicleData(newVehicleData) {
        updateElementInList(vehicles, setVehicles, newVehicleData)
        updateElementInList(laneInventory, setLaneInventory, newVehicleData)
        updateElementInList(draftVehicles, setDraftVehicles, newVehicleData)

        window.updateVehiclesInAssignInventory(newVehicleData)
    }

    function updateElementInList(list, setList, newVehicleData) {
        let existedVehicle = list.find(p => compareVehicles(p, newVehicleData))
        if (existedVehicle) {
            resetVehicleDetailInput(list)
            let index = list.indexOf(existedVehicle)
            existedVehicle = {...existedVehicle, ...newVehicleData}
            list[index] = existedVehicle
            setList((oldVehicles) =>  [...list])
        }
    }

    //#region Drag and Drop
    /**
     * Replace the vehicle in the Assigned column after user drag and drop the vehicle
     */
    function updateAssignedVehicleAfterDrop(dragData, dropData) {
        let vehicle = dragData.vehicle
        //Collapse vehicleDetail after perform drop
        vehicle.isVehicleDetailOpen = false

        //Skip if the vehicle is already assigned to this exact destination (no duplicate assignment)
        const isAlreadyAtDestination = vehicle.runNumber === dropData.runNumber && vehicle.laneNumber === dropData.laneNumber
        if (isAlreadyAtDestination) {
            return
        }

        //Sale type eligibility check is triggered once per assignment, keyed by a fresh assignmentInstanceId —
        //but only when the PAT integration is enabled for this auction; otherwise the vehicle behaves exactly
        //as it did before the PAT integration existed (no id assigned, no check triggered, no UI rendered).
        //If the vehicle already has a patAssignmentInstanceId (it's being moved between slots on the Assigned
        //side, not newly assigned), reuse it and skip re-triggering the check — the user's existing selection
        //is kept as-is.
        const alreadyHasPatAssignment = !!vehicle.patAssignmentInstanceId
        const patAssignmentInstanceId = isPatSaleTypeIntegrationEnabled
            ? (vehicle.patAssignmentInstanceId || uuid())
            : undefined
        if (isPatSaleTypeIntegrationEnabled) {
            vehicle.patAssignmentInstanceId = patAssignmentInstanceId
        }

        //Update draftVehicles for save
        let destinationRun = vehicles.find(p => p.runNumber === dropData.runNumber && p.laneNumber === dropData.laneNumber)
        let newVehicle = { ...vehicle, laneNumber: laneNumber, runNumber: destinationRun.runNumber, isUnsave: true, replacedItem: destinationRun, errorMessage: null, patAssignmentInstanceId }
        window.updateDraftVehicles(newVehicle)

        if (isPatSaleTypeIntegrationEnabled && !alreadyHasPatAssignment) {
            triggerPatEligibilityCheck(vehicle, patAssignmentInstanceId)
        }

        setVehicles((oldVehicles) => {
            let newVehicles = [...oldVehicles]
            let oldPosition = newVehicles.findIndex(p => compareVehicles(p, vehicle))
            let newPosition = oldVehicles.findIndex(p => p.runNumber === dropData.runNumber && p.laneNumber === dropData.laneNumber)

            //The destination item after dropped
            let destinationRun = newVehicles[newPosition]

            //The vehicle data with new runNumber
            let newVehicle = { ...vehicle, laneNumber: laneNumber, runNumber: destinationRun.runNumber, isUnsave: true, replacedItem: destinationRun, errorMessage: null }
            let oldVehicle

            const isHolderVehicle = holderVehicles.has(vehicle.runNumber)
            if(isHolderVehicle) {
                oldVehicle = holderVehicles.get(vehicle.runNumber)
            } else {
                oldVehicle = {status: "EMPTY", runNumber: vehicle.runNumber}
            }

            newVehicles[oldPosition] = oldVehicle
            newVehicles[newPosition] = newVehicle
            return newVehicles
        })

        //Remove the vehicle in Unassigned column if user drag vehicle from there
        window.removeUnassignedVehicleAfterDnD({vin: vehicle.vin, workOrderNumber: vehicle.workOrderNumber, sblu: vehicle.sblu})
        pushQuickSearchValue(EMPTY_STRING)
    }

    /**
     * Actually calls the sale type eligibility API for a single vehicle and dispatches the result.
     * Shared by the initial trigger (on assignment) and by a manual Retry.
     */
    function callPatEligibilityCheck(vehicle, patAssignmentInstanceId, triggerSource) {
        if (!vehicle.vin || inFlightPatRequestsRef.current.has(patAssignmentInstanceId)) {
            return
        }
        inFlightPatRequestsRef.current.add(patAssignmentInstanceId)

        getSaleTypesEligibility([{
            vin: vehicle.vin,
            auctionCode,
            lane: laneNumber,
            weightClass: vehicle.weightClass || null,
            vehicleType: vehicle.vehicleType || null,
            workOrderNumber: vehicle.workOrderNumber || null,
            saleYear: saleYear || null,
            saleNumber: saleNumber || null,
            sellerNumber: vehicle.sellerNumber || null,
            groupCode: vehicle.groupCode || null,
            altGroupCode: vehicle.altGroupCode || null,
            previousVehicleSaleType: vehicle.vehicleSaleType || null,
            triggerSource
        }])
            .then(response => {
                const result = response.data?.results?.[vehicle.vin]
                if (result && !result.error) {
                    dispatch(receivePatEligibilitySuccess(vehicle.vin, patAssignmentInstanceId, result))
                } else {
                    // result.error is an API-returned object, not a string - extract .message so the UI
                    // never renders "[object Object]" (matches the batch path in AssignInventoryDetail.js).
                    dispatch(receivePatEligibilityError(vehicle.vin, patAssignmentInstanceId, result?.error?.message || null))
                }
            })
            .catch((ex) => {
                // Lane not configured for PAT: the service has no eligibility decision to make,
                // not a failure to retry — treat it as a resolved "no override" result.
                if (ex?.response?.data?.error?.code === 'RESOURCE_NOT_FOUND') {
                    dispatch(receivePatEligibilitySuccess(vehicle.vin, patAssignmentInstanceId, { overrideEligible: false }))
                } else {
                    dispatch(receivePatEligibilityError(vehicle.vin, patAssignmentInstanceId, getPatErrorMessage(ex)))
                }
            })
            .finally(() => {
                inFlightPatRequestsRef.current.delete(patAssignmentInstanceId)
            })
    }

    /**
     * Trigger a sale type eligibility check for a single vehicle after it is assigned.
     * Guards against firing a duplicate request for the same assignment instance while one is already in flight.
     */
    function triggerPatEligibilityCheck(vehicle, patAssignmentInstanceId, triggerSource = 'assign') {
        dispatch(requestPatEligibility(vehicle.vin, patAssignmentInstanceId))
        callPatEligibilityCheck(vehicle, patAssignmentInstanceId, triggerSource)
    }

    /**
     * Re-run the sale type eligibility check for a vehicle when the user clicks "Retry" (FR-008).
     */
    function retryPatEligibilityCheck(vehicle) {
        if (!isPatSaleTypeIntegrationEnabled || !vehicle.patAssignmentInstanceId) {
            return
        }
        dispatch(retryPatEligibility(vehicle.vin, vehicle.patAssignmentInstanceId))
        callPatEligibilityCheck(vehicle, vehicle.patAssignmentInstanceId, 'retry')
    }

    /**
     * "Edit" on a vehicle already sent to AS400: attach a fresh patAssignmentInstanceId (same as a new
     * drop assignment) and trigger a sale type eligibility check, so the vehicle's SaleTypeEligibilityStatus
     * row appears for the user to review/override, exactly as if it had just been dropped.
     */
    function editPatSaleType(vehicle) {
        if (!isPatSaleTypeIntegrationEnabled) {
            return
        }
        const patAssignmentInstanceId = vehicle.patAssignmentInstanceId || uuid()
        const newVehicle = { ...vehicle, patAssignmentInstanceId }
        updateVehicleData(newVehicle)
        triggerPatEligibilityCheck(newVehicle, patAssignmentInstanceId, 'edit')
    }

    /**
     * "Cancel" out of an Edit: discard the in-progress eligibility entry and clear the
     * patAssignmentInstanceId, returning the vehicle to the state it was in when first loaded from AS400.
     */
    function cancelEditPatSaleType(vehicle) {
        if (vehicle.vin && vehicle.patAssignmentInstanceId) {
            dispatch(discardPatEligibility(vehicle.vin, vehicle.patAssignmentInstanceId))
        }
        updateVehicleData({ ...vehicle, patAssignmentInstanceId: null })
    }

    /**
     * US-Send-to-AS400-2nd (FR-001/FR-002/FR-005/FR-006): keep draftVehicles in sync with each
     * already-sent vehicle's live sale type edit state. SaleTypeEligibilityStatus dispatches
     * Select/Revert/Undo directly (its own useDispatch(), see PatEligibilityAction) with no callback
     * into this component, so this reacts to the resulting patEligibilityEntriesByKey state instead of
     * hooking each dispatch call site (research.md Decision 3). A dirty already-sent vehicle is
     * (re-)added to draftVehicles with resendSaleTypeOnly: true (never draftVehicle/isUnsave, so its
     * "already sent" rendering/non-draggable behavior is unaffected); a clean one - including after
     * Cancel, which clears patAssignmentInstanceId - is removed again.
     */
    useEffect(() => {
        let updatedDraftVehicles = draftVehicles
        vehicles.forEach(vehicle => {
            const isAlreadySentVehicle = !vehicle.draftVehicle && !vehicle.isUnsave
                && !isReserveVehicle(vehicle) && !isPreRegisterVehicle(vehicle)
            if (!isAlreadySentVehicle) {
                return
            }
            const entry = vehicle.patAssignmentInstanceId
                ? patEligibilityEntriesByKey[getPatEligibilityKey(vehicle.vin, vehicle.patAssignmentInstanceId)]
                : undefined
            updatedDraftVehicles = isSaleTypeChangedSinceLastSend(vehicle, entry)
                ? upsertElementInList(updatedDraftVehicles, { ...vehicle, resendSaleTypeOnly: true }, compareVehicles)
                : removeElementFromList(updatedDraftVehicles, vehicle, compareVehicles)
        })
        if (updatedDraftVehicles !== draftVehicles) {
            setDraftVehicles(updatedDraftVehicles)
        }
    }, [patEligibilityEntriesByKey, vehicles])

    /**
     * Move the vehicle from Assigned to Unassigned column when user click on button REMOVE
     */
    function handleRemoveVehicleEvent(data) {
        let itemIndex =  vehicles.findIndex(p => p.runNumber === data.runNumber
                                                    && p.laneNumber === data.laneNumber
                                                    && p.sblu === data.sblu)
        let vehicle = vehicles[itemIndex]

        //Discard any sale type eligibility result/in-flight request tied to this vehicle's current assignment
        if (vehicle.vin && vehicle.patAssignmentInstanceId) {
            dispatch(discardPatEligibility(vehicle.vin, vehicle.patAssignmentInstanceId))
        }

        let {isUnsave, patAssignmentInstanceId, ...restVehicle} = vehicle
        //Clear the stale run/lane assignment so a later re-drop into the same slot number isn't
        //mistaken by updateAssignedVehicleAfterDrop's isAlreadyAtDestination check for a no-op drop
        restVehicle = {...restVehicle, runNumber: null, laneNumber: null, draftVehicle: false, errorMessage: null, isVehicleDetailOpen: false}
        window.putUnassignedVehicleAfterDnD(restVehicle)
        window.resetUnassignVehicleCheckedStatus(restVehicle.checked)
        window.resetSortCriterias()

        //Remove the vehicle from draft list
        window.removeFromDraftVehicles(vehicle)

        const newVehicles = [...vehicles]
        const isHolderVehicle = holderVehicles.has(vehicle.runNumber)
        if(isHolderVehicle) {
            newVehicles[itemIndex] = holderVehicles.get(vehicle.runNumber)
        } else {
            newVehicles[itemIndex] = {status: "EMPTY", runNumber: data.runNumber}
        }

        setVehicles(newVehicles)
        pushQuickSearchValue(EMPTY_STRING)
    }

    //#endregion

    function handleShowMoreEmptyRuns() {
        addEmptyRunToExistingVehicles(RUN_LANE_NUMBER_OF_SHOW_MORE)
    }

    function handleShowAllEmptyRuns() {
        addEmptyRunToExistingVehicles(RUN_LANE_MAX_LENGTH)
    }

    function addEmptyRunToExistingVehicles(numberOfRun) {
        setVehicles((oldVehicles) => {
            let newVehicles = [...oldVehicles]
            let lastVehicle = newVehicles.slice(-1)
            let lastRunNumber = lastVehicle[0]?.runNumber
            let lastEmptySpotPosition = emptyRuns.findIndex(p => Number(p) === Number(lastRunNumber))
            if (lastEmptySpotPosition > 0) {
                addEmptyRuns(newVehicles, emptyRuns, lastEmptySpotPosition + 1, numberOfRun)
            }
            return newVehicles
        })
    }

    function canLoadMoreEmptyVehicle() {
        const maxRun = vehicles.slice(-1)[0]?.runNumber
        return isShowChecked && !isEmpty(runRanges) && Number(maxRun) < RUN_LANE_MAX_LENGTH
    }

    function handleOnClickOpenPregistTration(sblu, linktoSeller, title) {
        setIsGetPreRegistError(false)
        getPreRegistrations(auctionCode, sblu, linktoSeller)
            .then((response) => {
                setIsPreRegistrationOpen(true)
                setTitleVehicle(title)
                setPreRegistrationData(response.data)
            }).catch((ex) => {
            setIsGetPreRegistError(true)
        })
    }

    const onUpdatePreRegistrationInAS400 = (newVehicleData) => {
        updatePreRegistrationData(newVehicleData);
        setIsPreRegistrationOpen(false);
    }

    const onDeletePreRegistrationFromAS400 = () => {
        updatePreRegistrationData({vin:""});
        setIsPreRegistrationOpen(true);
    }

    useEffect(()=>{
        if(bulkUpdateVehicleErrors.length){
            const errorVehicles = [...vehicles]
            bulkUpdateVehicleErrors.forEach((error)=>{
                    const index =  errorVehicles.findIndex(p => p.workOrderNumber && (p.workOrderNumber.toString() === error.workOrderNumber.toString()))
                    if (index > -1) {
                        errorVehicles[index].errorMessage = error.errorMessage
                    }
            })
            setVehicles(errorVehicles)
        }
    },[bulkUpdateVehicleErrors])
    
    function updateHolderVehicles(vehicle) {
        holderVehicles.set(selectedVehicle.runNumber, vehicle)
        setHolderVehicles(holderVehicles)
    }

    /**
     * Reset status isUnsave for dropped vehicles
     *
     * Uses the functional setVehicles(prev => ...) form deliberately: this is called back-to-back with
     * window.resetPatAssignmentAfterSaveDraft after a successful "Save As Draft" (see AssignInventoryDetail.js),
     * and both functions read `vehicles` from their own render-time closure. Computing from a plain
     * `vehicles` reference here would race with the other call's setVehicles - whichever runs second
     * would silently discard the first call's change, since neither call's closure sees the other's
     * pending update. The functional form always operates on the latest pending state, so the two
     * calls compose correctly regardless of order (bug found in manual testing: a freshly-dropped
     * vehicle's dropdown/CANCEL kept showing after Save As Draft, because this function's stale-closure
     * update ran after and clobbered resetPatAssignmentAfterSaveDraft's).
     *
     * The updater itself stays pure - no setDraftVehicles call inside it - since React requires
     * updaters to only compute and return the next state (and double-invokes them in Strict Mode dev
     * to catch violations of this). The derived draftVehicles list is computed once, outside the
     * updater, from its return value.
     */
    window.resetDroppedVehicles = function() {
        let computedDraftVehicles = null
        setVehicles(prevVehicles => {
            let hasChange = false
            const resetedVehicles = prevVehicles.map(vehicle => {
                                        if (vehicle.isUnsave) {
                                            let {isUnsave, ...restVehicle} = vehicle
                                            // update vehicle after save as draft
                                            restVehicle = {...restVehicle,
                                                draftVehicle: true,
                                                origSaleYear: (vehicle.draftVehicle ? vehicle.origSaleYear : vehicle.fromVehicle.saleYear) || 0,
                                                origSaleNumber: (vehicle.draftVehicle ? vehicle.origSaleNumber : vehicle.fromVehicle.saleNumber) || 0,
                                                origLaneNumber: (vehicle.draftVehicle ? vehicle.origLaneNumber : vehicle.fromVehicle.laneNumber) || 0,
                                                origRunNumber: (vehicle.draftVehicle ? vehicle.origRunNumber : vehicle.fromVehicle.runNumber) || 0,
                                            }
                                            hasChange = true
                                            return restVehicle
                                        }
                                        return vehicle
                                    })

            if (!hasChange) {
                return prevVehicles
            }
            computedDraftVehicles = resetedVehicles.filter(vehicle => vehicle.draftVehicle === true)
            return resetedVehicles
        })
        if (computedDraftVehicles) {
            setDraftVehicles(computedDraftVehicles)
        }
    }
    
    /**
     * Reset status for vehicles sent to AS400.
     *
     * FR-015a: also update each vehicle's FR-015 display in place — clearing `patAssignmentInstanceId`
     * (so the row falls back from the FR-006 live dropdown view to the FR-015 read-only chip, exactly
     * as a fresh "GO" search would leave it) and setting `vehicleSaleType` to the reference code
     * actually just sent for it, per `saleTypeBySblu` (built by the caller from the persist request
     * body). A vehicle omitted from `saleTypeBySblu` (never went through this feature's PAT flow this
     * session) keeps its existing `vehicleSaleType`/`patAssignmentInstanceId` untouched.
     *
     * Also clears `patAssignmentInstanceId` for any vehicle that was Edit-clicked (or Retried) but
     * never actually changed its sale type - these are excluded from the send batch entirely (see the
     * dirty-tracking effect above), so they're never in `sbluList`, yet their Edit UI state was
     * otherwise left dangling open after Send to AS400 with no way back to the read-only chip short of
     * an explicit Cancel. Treating a clean Edit session as implicitly cancelled once Send to AS400 is
     * clicked matches Cancel's own behavior exactly (same field cleared, same effect).
     */
    window.resetVehiclesSentToAS400 = function(sbluList, saleTypeBySblu = {}) {
        let hasChange = false
        const resetVehicles = vehicles
            .map(vehicle => {
                                    if (includes(sbluList, vehicle.sblu)) {
                                        let restVehicle = {...vehicle, draftVehicle: false, isUnsave: false, resultType: 'R'}
                                        if (vehicle.sblu in saleTypeBySblu) {
                                            restVehicle.patAssignmentInstanceId = null
                                            restVehicle.vehicleSaleType = saleTypeBySblu[vehicle.sblu]
                                        }
                                        hasChange = true
                                        return restVehicle
                                    }
                                    if (vehicle.patAssignmentInstanceId) {
                                        const entry = patEligibilityEntriesByKey[getPatEligibilityKey(vehicle.vin, vehicle.patAssignmentInstanceId)]
                                        if (!isSaleTypeChangedSinceLastSend(vehicle, entry)) {
                                            hasChange = true
                                            return { ...vehicle, patAssignmentInstanceId: null }
                                        }
                                    }
                                    return vehicle
                                })

        if(hasChange) {
            setVehicles(resetVehicles)
        }
    }

    /**
     * US2054837 bug fix: mirrors resetVehiclesSentToAS400's display-reset above, but for "Save As
     * Draft" instead of "Send to AS400" — without this, a vehicle with a live PAT check running this
     * session (patAssignmentInstanceId set) kept showing the interactive dropdown/Revert/Recommend
     * controls (with "CANCEL" instead of "EDIT") after a successful save, instead of falling back to
     * the same static, read-only FR-007 Chip a fresh "GO" search would show. Clears
     * `patAssignmentInstanceId` and stamps `patSelectedSaleType`/`patLabel` with the exact snapshot
     * just persisted (per `patSnapshotBySblu`, built by the caller from the save-draft request body),
     * so the row immediately matches what reloading would show, with no extra round trip.
     *
     * Uses the functional setVehicles(prev => ...) form deliberately — see the matching note on
     * window.resetDroppedVehicles above, which this is always called alongside; both must use the
     * functional form so neither silently discards the other's change via a stale-closure `vehicles`.
     */
    window.resetPatAssignmentAfterSaveDraft = function(patSnapshotBySblu = {}) {
        setVehicles(prevVehicles => {
            let hasChange = false
            const resetVehicles = prevVehicles
                .map(vehicle => {
                    if (vehicle.sblu in patSnapshotBySblu) {
                        hasChange = true
                        return {
                            ...vehicle,
                            patAssignmentInstanceId: null,
                            patSelectedSaleType: patSnapshotBySblu[vehicle.sblu].patSelectedSaleType,
                            patLabel: patSnapshotBySblu[vehicle.sblu].patLabel
                        }
                    }
                    return vehicle
                })

            return hasChange ? resetVehicles : prevVehicles
        })
    }

    const isDisplayNone = useCallback((vehicle) => {
        return isDragging
            && draggingItem
            && compareVehicles(draggingItem, vehicle)
    }, [isDragging, draggingItem]);

    // Remove item from the list help to decrease re-render items
    // Support to improve performance
    const visibleVehicles = useMemo(() => {
        const isEmptyVehicleVisible = (vehicle) => !isNil(vehicle)
            && vehicle.status === "EMPTY"
            && !!isShowChecked
            && !isEmpty(runRanges)

        const isAssignVehicleVisible = (vehicle) => !isNil(vehicle)
            && vehicle.status !== "EMPTY"

        return [...vehicles].filter(vehicle => isEmptyVehicleVisible(vehicle) || isAssignVehicleVisible(vehicle))
    }, [vehicles, isShowChecked, runRanges])

    useEffect(() => {
        window.updateQuickSearchData(visibleVehicles, true)

    }, [visibleVehicles])

    useEffect(() => {
        if (scrollToItem > -1) {
            assignedVehiclesRef.current?.scrollToItem(scrollToItem)
        }
    }, [scrollToItem]);

    //#region Auto assign selected runs
    window.replaceReserveVehicleWhenAutoAssign = function(reserveVehicleSpots) {
        let newVehicles = [...vehicles]
        reserveVehicleSpots.forEach((vehicleToMove, runNumber) => {
            let index = newVehicles.findIndex(p => p.runNumber === runNumber)
            if (index > -1) {
                newVehicles[index] = vehicleToMove
            }
        })

        setVehicles(newVehicles)
    }

    window.getReservedSpots = function(maxLength, startingRun) {
        let vehiclesCloned = [...vehicles]
        vehiclesCloned = orderBy(vehiclesCloned, ['runNumber'], 'asc')
        let reservedVehicles = []
        if (maxLength > vehiclesCloned.length) {
            maxLength = vehiclesCloned.length
        }

        const availableRunNumber = getAvailableReserveForAssign()
        for (let i = 0; i < vehiclesCloned.length; i++) {
            const currentVehicle = vehiclesCloned[i]
            let status = currentVehicle.type ? currentVehicle.type : currentVehicle.status
            if (status === 'SP' && currentVehicle.vin === ''
                    && Number(currentVehicle.runNumber) >= Number(startingRun) &&
                    availableRunNumber.includes(currentVehicle.runNumber)) {
                if (reservedVehicles.length < maxLength) {
                    reservedVehicles.push(currentVehicle)
                } else {
                    return reservedVehicles
                }
            }
        }

        return reservedVehicles
    }

    function getAvailableReserveForAssign() {
        return vehicles.filter(p => isReserveVehicle(p)).map(p => p.runNumber)
    }
    //#endregion

    return (
        <>
            {!!vehicles && !isEmpty(vehicles) &&
                <DynamicSizeList data={visibleVehicles} isAllowAutoScroll={true} ref={assignedVehiclesRef}>
                    {(vehicle, index) => (
                        <>
                            {!isNil(vehicle) && vehicle.status === "EMPTY" && !!isShowChecked && !isEmpty(runRanges) &&
                                <EmptyRun
                                    laneNumber={laneNumber}
                                    vehicle={vehicle}
                                    index={index}
                                    updateAssignedVehicleAfterDrop={(dragData) => updateAssignedVehicleAfterDrop(dragData, vehicle)}
                                />
                            }
                            {!isNil(vehicle) && vehicle.status !== "EMPTY" && (
                                <div className={classNames(
                                    {'dsp-n': isDisplayNone(vehicle)}
                                )}>
                                    <AssignInventoryVehicleItem
                                        vehicle={vehicle}
                                        messageUpdateVehicleSuccessful={messageUpdateVehicleSuccessful}
                                        handleVcfOnClick={(sblu) => {
                                            setSblu(sblu)
                                            if(isPreRegisterVehicle(vehicle)) {
                                                setIsVcfEditOpen(true)
                                            } else {
                                                setIsVcfOpen(true)
                                            }
                                        }}
                                        handleOnClickOpenPregistTration={(sblu, linktoSeller, title) => {
                                            setSelectedVehicle(vehicle);
                                            return handleOnClickOpenPregistTration(sblu, linktoSeller, title)}}
                                        dragData={{dragType: DnDDragTypeConstant.ASSIGN_VEHICLE, itemIndex: index}}
                                        canRemove={true}
                                        handleRemoveVehicleEvent={handleRemoveVehicleEvent}
                                        index={index}
                                        updateAssignedVehicleAfterDrop={(dragData) => updateAssignedVehicleAfterDrop(dragData, vehicle)}
                                        onRetryPatEligibility={() => retryPatEligibilityCheck(vehicle)}
                                        onEditPatSaleType={() => editPatSaleType(vehicle)}
                                        onCancelEditPatSaleType={() => cancelEditPatSaleType(vehicle)}
                                        isPatSaleTypeIntegrationEnabled={isPatSaleTypeIntegrationEnabled}
                                        isSendToAS400Blocked={isSendToAS400Blocked}
                                        setMessageUpdateVehicleSuccessful={setMessageUpdateVehicleSuccessful}
                                        allowQuickSearch={true}
                                        quickSearchText={quickSearchContext.quickSearchValue}
                                        quickSearchIndex={scrollToItem}
                                        updateVehicleData={updateVehicleData}
                                        isVehicleDetailParticular={true}
                                        setGetVehicleError={() => {}}
                                        isShownSellerSearchButton={false}
                                    />
                                </div>

                            )}
                            {canLoadMoreEmptyVehicle() && (index === vehicles.length - 1) &&
                                <AssignVehicleListFooter
                                    onShowMoreEmptyRuns={handleShowMoreEmptyRuns}
                                    onShowAllEmptyRuns={handleShowAllEmptyRuns}
                                />
                            }
                        </>
                    )}
                </DynamicSizeList>
            }

            {!!isVcfOpen && (
                <VehicleVcf
                    isVcfOpen={isVcfOpen}
                    setIsVcfOpen={setIsVcfOpen}
                    auctionCode={auctionCode}
                    sbluNumber={sblu}
                />
            )}
            {!!isVcfEditOpen && (
                <VehicleVcfEdit
                    isVcfEditOpen={isVcfEditOpen}
                    setIsVcfEditOpen={setIsVcfEditOpen}
                    auctionCode={auctionCode}
                    sbluNumber={sblu}
                />
            )}

            {!!isPreRegistrationOpen && (
                <PreRegistration
                    isPreRegistrationOpen={isPreRegistrationOpen}
                    setIsPreRegistrationOpen={setIsPreRegistrationOpen}
                    auctionCode={auctionCode}
                    preRegistrationData={preRegistrationData}
                    titleVehicle={titleVehicle}
                    onDeleteFromAS400={onDeletePreRegistrationFromAS400}
                    onUpdateInAS400={onUpdatePreRegistrationInAS400}
                />
            )}
        </>
    )

    function updatePreRegistrationData(newPreRegistrationData) {
        const index = vehicles.findIndex(vehicle =>
            vehicle.laneNumber === selectedVehicle.laneNumber &&
            vehicle.runNumber === selectedVehicle.runNumber &&
            vehicle.sblu === selectedVehicle.sblu
        );
        if (index > -1) {
            vehicles[index] = {...vehicles[index], ...newPreRegistrationData};
            updateHolderVehicles(vehicles[index])
            setVehicles((prevState) => [...vehicles]);
            dispatch(getVehicleAfterUpdatePreRegist({id: `vehicle-item-${vehicles[index].vin}-${vehicles[index].workOrderNumber}-${vehicles[index].runNumber}`}))
        }

        pushQuickSearchValue(EMPTY_STRING)
    }
}
